/**
 * LEARNING PAGE - FLO SIGNATURE DESIGN SYSTEM v2
 * Clean. Controlled. Consistent. Dark tool mode.
 */

import React, { useState } from 'react';
import { PlusIcon } from '@heroicons/react/24/outline';
import { Screen, Section, Button, Tabs } from '../design/primitives';
import { SPACING } from '../design/tokens';
import BookList from '../components/BookList';
import CourseList from '../components/CourseList';
import VideoList from '../components/VideoList';
import ArticleList from '../components/ArticleList';
import NewsletterList from '../components/NewsletterList';
import BookForm from '../components/forms/BookForm';
import CourseForm from '../components/forms/CourseForm';
import VideoForm from '../components/forms/VideoForm';
import ArticleForm from '../components/forms/ArticleForm';

type TabType = 'books' | 'courses' | 'videos' | 'articles' | 'newsletters';

const LearningPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabType>('books');
  const [isBookFormOpen, setIsBookFormOpen] = useState(false);
  const [isCourseFormOpen, setIsCourseFormOpen] = useState(false);
  const [isVideoFormOpen, setIsVideoFormOpen] = useState(false);
  const [isArticleFormOpen, setIsArticleFormOpen] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const handleItemCreated = () => {
    setRefreshKey(prev => prev + 1);
    setIsBookFormOpen(false);
    setIsCourseFormOpen(false);
    setIsVideoFormOpen(false);
    setIsArticleFormOpen(false);
  };

  const handleNewClick = () => {
    switch (activeTab) {
      case 'books':
        setIsBookFormOpen(true);
        break;
      case 'courses':
        setIsCourseFormOpen(true);
        break;
      case 'videos':
        setIsVideoFormOpen(true);
        break;
      case 'articles':
        setIsArticleFormOpen(true);
        break;
    }
  };

  return (
    <Screen>
      <Section
        title="Learning"
        action={
          activeTab !== 'newsletters' ? (
            <Button
              variant="primary"
              onClick={handleNewClick}
              icon={<PlusIcon style={{ width: '16px', height: '16px' }} />}
            >
              New {activeTab.charAt(0).toUpperCase() + activeTab.slice(1, -1)}
            </Button>
          ) : undefined
        }
      >
        <Tabs
          tabs={[
            { id: 'books', label: 'Books' },
            { id: 'courses', label: 'Courses' },
            { id: 'videos', label: 'Videos' },
            { id: 'articles', label: 'Articles' },
            { id: 'newsletters', label: 'Newsletters' },
          ]}
          activeTab={activeTab}
          onChange={(tab) => setActiveTab(tab as TabType)}
        />

        <div style={{ marginTop: SPACING[24] }}>
          {activeTab === 'books' && <BookList key={refreshKey} />}
          {activeTab === 'courses' && <CourseList key={refreshKey} />}
          {activeTab === 'videos' && <VideoList key={refreshKey} />}
          {activeTab === 'articles' && <ArticleList key={refreshKey} />}
          {activeTab === 'newsletters' && <NewsletterList key={refreshKey} />}
        </div>
      </Section>

      <BookForm
        isOpen={isBookFormOpen}
        onClose={() => setIsBookFormOpen(false)}
        onBookCreated={handleItemCreated}
      />
      <CourseForm
        isOpen={isCourseFormOpen}
        onClose={() => setIsCourseFormOpen(false)}
        onCourseCreated={handleItemCreated}
      />
      <VideoForm
        isOpen={isVideoFormOpen}
        onClose={() => setIsVideoFormOpen(false)}
        onVideoCreated={handleItemCreated}
      />
      <ArticleForm
        isOpen={isArticleFormOpen}
        onClose={() => setIsArticleFormOpen(false)}
        onArticleCreated={handleItemCreated}
      />
    </Screen>
  );
};

export default LearningPage;
